import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { isMobile } from 'react-device-detect';
import { TYPE_ORDER, getCardPrimaryType } from './deckTypeUtils';
import './DeckVisualView.css';

// Scryfall small image aspect ratio (height / width = 204 / 146)
const SCRYFALL_ASPECT_RATIO = 204 / 146;

// Card display size options (width in px, height derived from aspect ratio)
const CARD_DISPLAY_SIZES = {
  small:  { width: 150, height: Math.round(150 * SCRYFALL_ASPECT_RATIO) },
  normal: { width: 200, height: Math.round(200 * SCRYFALL_ASPECT_RATIO) },
  large:  { width: 280, height: Math.round(280 * SCRYFALL_ASPECT_RATIO) },
};

// Visual stack layout
const CARD_SCALE = isMobile ? 0.7 : 1;

// Sentinel dataTransfer type used to distinguish internal card repositioning drags
// from external card drops (e.g. dragging an image from EDHREC/TCGPlayer).
// Presence of this type in dataTransfer.types means the drag originated inside
// DeckVisualView and must NOT trigger an add-to-collection action.
const INTERNAL_DRAG_TYPE = 'application/x-internal-deck-card';

const FOIL_FINISHES = new Set(['foil', 'etched']);

const COLORS = {
  dropHighlight: '#3498db',
  dropHighlightBg: 'rgba(52, 152, 219, 0.1)',
};
const Z_INDEX = { dropOverlay: 100 };
const BORDER_RADIUS = 4;
const CARD_OFFSET = isMobile ? 20 : 30;
const MIN_DROP_AREA_HEIGHT = 300;
const MOBILE_STACK_MAX_HEIGHT = 500;

// Hover preview
const PREVIEW_IMG_WIDTH = 244;
const PREVIEW_IMG_HEIGHT = 340;
const PREVIEW_PANEL_WIDTH = 300;
const PREVIEW_ESTIMATED_HEIGHT = 400;
const PREVIEW_GAP = 16;
const PREVIEW_EDGE_MARGIN = 10;
const PREVIEW_TOP_OFFSET = 100;

// Timing
const PREVIEW_DISMISS_DELAY_MS = 50;

const CardImage = ({ entry, onCardClick, navigate, onPreview, onClearPreview, cardWidth, cardHeight }) => {
  const handleClick = useCallback(() => {
    if (onCardClick) {
      onCardClick(entry);
    } else if (entry.scryfall_id) {
      navigate(`/cardsearch/${entry.scryfall_id}`);
    }
  }, [entry, onCardClick, navigate]);

  const handleMouseEnter = useCallback((e) => {
    if (!isMobile && entry.image_normal) {
      const rect = e.currentTarget.getBoundingClientRect();
      onPreview(entry, rect);
    }
  }, [entry, onPreview]);

  const handleMouseLeave = useCallback((e) => {
    if (!isMobile) {
      // Ignore leaves to browser chrome (scrollbars, etc.) — relatedTarget is null when
      // the mouse moves to OS-level UI outside any DOM element.
      if (e.relatedTarget === null) return;
      onClearPreview();
    }
  }, [onClearPreview]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleClick();
    }
  }, [handleClick]);

  const [tapped, setTapped] = useState(false);
  const handleTouchEnd = useCallback((e) => {
    if (!isMobile) return;
    if (e.cancelable) {
      e.preventDefault();
    }
    if (!tapped && entry.image_normal) {
      setTapped(true);
      onPreview(entry, e.currentTarget.getBoundingClientRect());
    } else {
      setTapped(false);
      onClearPreview();
      handleClick();
    }
  }, [tapped, entry, onPreview, onClearPreview, handleClick]);

  const isFoil = FOIL_FINISHES.has(entry.finish);

  return (
    <div
      className="dvv-card-image"
      style={{ width: cardWidth, height: cardHeight }}
      role="button"
      tabIndex={0}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={!isMobile ? handleClick : undefined}
      onKeyDown={!isMobile ? handleKeyDown : undefined}
      onTouchEnd={isMobile ? handleTouchEnd : undefined}
    >
      {entry.image_small ? (
        <img
          src={entry.image_small}
          alt={entry.name}
          width={cardWidth}
          height={cardHeight}
          className="dvv-card-img"
          loading="lazy"
          draggable={false}
        />
      ) : (
        <div className="dvv-card-placeholder" style={{ width: cardWidth, height: cardHeight }}>
          {entry.name}
        </div>
      )}
      {isFoil && <div className="dvv-foil-overlay" aria-hidden="true" />}
      {(entry.quantity || 1) > 1 && (
        <span className="dvv-qty-badge">x{entry.quantity}</span>
      )}
    </div>
  );
};

const CardImageMemo = React.memo(CardImage);

const HoverPreview = ({ entry, position }) => {
  const [normalLoaded, setNormalLoaded] = useState(false);

  if (!entry || !position) return null;

  const previewLeft = position.right + PREVIEW_GAP;
  const fitsRight = previewLeft + PREVIEW_PANEL_WIDTH < window.innerWidth;
  const previewTop = Math.min(
    Math.max(PREVIEW_EDGE_MARGIN, position.top - PREVIEW_TOP_OFFSET),
    window.innerHeight - PREVIEW_ESTIMATED_HEIGHT - PREVIEW_EDGE_MARGIN
  );

  return (
    <div
      className="dvv-preview"
      style={{
        left: fitsRight ? previewLeft : position.left - PREVIEW_PANEL_WIDTH - PREVIEW_GAP,
        top: previewTop,
      }}
    >
      {!normalLoaded && entry.image_small && (
        <img
          src={entry.image_small}
          alt={entry.name}
          width={PREVIEW_IMG_WIDTH}
          height={PREVIEW_IMG_HEIGHT}
          className="dvv-card-img"
        />
      )}
      {entry.image_normal && (
        <img
          src={entry.image_normal}
          alt={entry.name}
          width={PREVIEW_IMG_WIDTH}
          height={PREVIEW_IMG_HEIGHT}
          className="dvv-card-img"
          style={{ display: normalLoaded ? 'block' : 'none' }}
          onLoad={() => setNormalLoaded(true)}
        />
      )}
      <div className="dvv-preview-info">
        <div className="dvv-preview-name">{entry.name}</div>
        <div className="dvv-preview-type">{entry.type_line}</div>
        {entry.current_price && (
          <div className="dvv-preview-price">{entry.current_price}</div>
        )}
      </div>
    </div>
  );
};

const DeckVisualView = ({ sortedData, groupedData = null, navigate, onCardClick, onCardDrop, cardDisplaySize = 'normal' }) => {
  const sizeKey = CARD_DISPLAY_SIZES[cardDisplaySize] ? cardDisplaySize : 'normal';
  const cardWidth = Math.round(CARD_DISPLAY_SIZES[sizeKey].width * CARD_SCALE);
  const cardHeight = Math.round(CARD_DISPLAY_SIZES[sizeKey].height * CARD_SCALE);
  const [previewEntry, setPreviewEntry] = useState(null);
  const [previewPosition, setPreviewPosition] = useState(null);
  const [dragOverActive, setDragOverActive] = useState(false);
  const clearTimerRef = useRef(null);

  const handlePreview = useCallback((entry, rect) => {
    if (clearTimerRef.current) {
      clearTimeout(clearTimerRef.current);
      clearTimerRef.current = null;
    }
    setPreviewEntry(entry);
    setPreviewPosition(rect);
  }, []);

  const handleClearPreview = useCallback(() => {
    clearTimerRef.current = setTimeout(() => {
      setPreviewEntry(null);
      setPreviewPosition(null);
    }, PREVIEW_DISMISS_DELAY_MS);
  }, []);

  // Dismiss the card detail panel on Escape key press.
  useEffect(() => {
    if (isMobile) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && previewEntry) {
        if (clearTimerRef.current) clearTimeout(clearTimerRef.current);
        setPreviewEntry(null);
        setPreviewPosition(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [previewEntry]);

  const typeGroups = useMemo(() => {
    if (groupedData) {
      return groupedData.map((group) => ({
        type: group.sortKey,
        label: group.label,
        cards: group.cards,
        totalCount: group.totalCount,
      }));
    }

    const groups = {};
    TYPE_ORDER.forEach((type) => { groups[type] = []; });

    sortedData.forEach((entry) => {
      const type = getCardPrimaryType(entry.type_line);
      groups[type].push(entry);
    });

    return TYPE_ORDER
      .filter((type) => groups[type].length > 0)
      .map((type) => ({
        type,
        label: type === 'Other' ? 'Other' : type + 's',
        cards: groups[type],
        totalCount: groups[type].reduce((sum, e) => sum + (e.quantity || 1), 0),
      }));
  }, [sortedData, groupedData]);

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Internal repositioning drags should not show the "copy" drop effect.
    if (e.dataTransfer.types.includes(INTERNAL_DRAG_TYPE)) {
      e.dataTransfer.dropEffect = 'move';
    } else {
      e.dataTransfer.dropEffect = 'copy';
    }
    setDragOverActive(true);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only show the drop-target highlight for external drags.
    if (!e.dataTransfer.types.includes(INTERNAL_DRAG_TYPE)) {
      setDragOverActive(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverActive(false);
    // If the drag originated inside DeckVisualView (internal reposition), do NOT
    // forward the event to onCardDrop — that handler would trigger an add-to-collection
    // API call which is incorrect for internal repositioning.
    if (e.dataTransfer.types.includes(INTERNAL_DRAG_TYPE)) {
      return;
    }
    if (onCardDrop) {
      onCardDrop(e);
    }
  };

  const handleContainerMouseLeave = useCallback((e) => {
    // When the mouse leaves the entire DeckVisualView container to another DOM element
    // (not to browser chrome), clear the preview.
    if (!isMobile && e.relatedTarget !== null) {
      handleClearPreview();
    }
  }, [handleClearPreview]);

  return (
    <div
      onMouseLeave={handleContainerMouseLeave}
      onDragOver={handleDragOver}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      style={{ position: 'relative', minHeight: MIN_DROP_AREA_HEIGHT }}
    >
      <div
        className={`dvv-grid ${isMobile ? 'dvv-grid--mobile' : 'dvv-grid--desktop'}`}
        style={{
          gridTemplateColumns: isMobile ? undefined : `repeat(auto-fill, minmax(${cardWidth}px, 1fr))`,
        }}
      >
        {typeGroups.map(({ type, label, cards, totalCount }) => (
          <div key={type} className="dvv-type-group">
            <div className="dvv-type-header">
              {label} ({totalCount})
            </div>
            <div
              className="dvv-stack"
              style={{
                height: cardHeight + (cards.length - 1) * CARD_OFFSET,
                maxHeight: isMobile ? MOBILE_STACK_MAX_HEIGHT : undefined,
                overflowY: isMobile ? 'auto' : undefined,
              }}
            >
              {cards.map((entry, idx) => (
                <div
                   key={`${entry.id || entry.scryfall_id}-${idx}`}
                   className="dvv-stack-card"
                   style={{ top: idx * CARD_OFFSET, zIndex: idx }}
                   draggable
                   onDragStart={(e) => {
                     // Tag this drag as originating inside DeckVisualView so the drop
                     // handler can distinguish it from an external add-to-collection drag.
                     e.dataTransfer.setData(INTERNAL_DRAG_TYPE, entry.id || entry.scryfall_id || '');
                     e.dataTransfer.effectAllowed = 'move';
                   }}
                 >
                   <CardImageMemo
                     entry={entry}
                     onCardClick={onCardClick}
                     navigate={navigate}
                     onPreview={handlePreview}
                     onClearPreview={handleClearPreview}
                     cardWidth={cardWidth}
                     cardHeight={cardHeight}
                   />
                 </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {dragOverActive && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          border: `2px dashed ${COLORS.dropHighlight}`,
          backgroundColor: COLORS.dropHighlightBg,
          borderRadius: BORDER_RADIUS,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: Z_INDEX.dropOverlay,
          pointerEvents: 'none',
        }}>
          <div style={{ textAlign: 'center', color: COLORS.dropHighlight, fontWeight: 'bold' }}>
            📎 Drop card here
          </div>
        </div>
      )}

      <HoverPreview entry={previewEntry} position={previewPosition} />
    </div>
  );
};

export default DeckVisualView;
